// Eingebaute Skizzen-Instrumente. Sie sind bewusst einfach: Der echte Klang kommt später
// über die Bridge aus den eigenen Plugins (Nexus, Tonehub, Sample-Bibliothek), gerendert auf dem Studio-Rechner.
const mtof = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

export type Voice = (ctx: BaseAudioContext, out: AudioNode, midi: number, vel: number, t: number, dur: number) => void;

let noiseBuf: AudioBuffer | null = null;
function noise(ctx: BaseAudioContext) {
  if (noiseBuf && noiseBuf.sampleRate === ctx.sampleRate) return noiseBuf;
  noiseBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = noiseBuf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return noiseBuf;
}

const env = (g: GainNode, t: number, peak: number, a: number, dur: number, r: number) => {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + a);
  g.gain.setTargetAtTime(peak * 0.7, t + a, dur * 0.4 + 0.01);
  g.gain.setTargetAtTime(0.0001, t + dur, r);
};

const synth = (type: OscillatorType, cutoff: number, detune: number, a: number, r: number, gain: number, oct = 0): Voice =>
  (ctx, out, midi, vel, t, dur) => {
    const g = ctx.createGain();
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(cutoff * (0.6 + vel), t);
    f.frequency.exponentialRampToValueAtTime(Math.max(cutoff * 0.35, 200), t + dur + r);
    f.connect(g).connect(out);
    for (const d of detune ? [-detune, detune] : [0]) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = mtof(midi + oct * 12);
      o.detune.value = d;
      o.connect(f);
      o.start(t);
      o.stop(t + dur + r * 6);
    }
    env(g, t, gain * vel, a, dur, r);
  };

export const PRESETS: Record<string, Voice> = {
  bass: synth('sawtooth', 900, 4, 0.005, 0.05, 0.5),
  keys: synth('triangle', 3200, 6, 0.01, 0.35, 0.22),
  lead: synth('square', 2600, 8, 0.01, 0.12, 0.18),
  pad: synth('sawtooth', 1800, 12, 0.4, 0.8, 0.12),
};

export const drum: Voice = (ctx, out, midi, vel, t) => {
  const g = ctx.createGain();
  g.connect(out);
  if (midi === 36 || midi === 35) {
    const o = ctx.createOscillator();
    o.frequency.setValueAtTime(140, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
    o.connect(g);
    g.gain.setValueAtTime(vel, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    o.start(t); o.stop(t + 0.4);
  } else {
    const n = ctx.createBufferSource();
    n.buffer = noise(ctx);
    const f = ctx.createBiquadFilter();
    f.type = midi === 38 || midi === 40 ? 'bandpass' : 'highpass';
    f.frequency.value = midi === 38 || midi === 40 ? 1800 : 7000;
    n.connect(f).connect(g);
    const len = midi === 38 || midi === 40 ? 0.18 : 0.05;
    g.gain.setValueAtTime(vel * 0.7, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + len);
    n.start(t); n.stop(t + len + 0.02);
  }
};

export function voiceFor(plugin?: string, preset?: string): Voice {
  if (plugin === 'srs:drum') return drum;
  return PRESETS[preset ?? 'keys'] ?? PRESETS.keys;
}
